import { useFrame, useThree } from '@react-three/fiber'
import { useSiteModelContext } from 'features/siteMap/contexts/SiteModelContext'
import isNil from 'lodash/isNil'
import React, { FC, useMemo, useRef } from 'react'
import {
  MathUtils,
  Object3D,
  PerspectiveCamera,
  Quaternion,
  Vector3,
} from 'three'
import {
  isSiteFeaturePrimitive,
  SiteFeaturePrimitive,
  SiteFeaturePrimitiveBox,
  SiteFeaturePrimitiveCylinder,
  SiteFeaturePrimitiveSphere,
} from 'types'

import { useTilesetMapFeatureHelpers } from './useTilesetMapFeatureHelpers'

interface CameraFlightHandlerProps {
  unitIDFlyingTo: string | undefined
  setUnitIDFlyingTo: (unitID: string | undefined) => void
}

export const CameraFlightHandler: FC<CameraFlightHandlerProps> = ({
  unitIDFlyingTo,
  setUnitIDFlyingTo,
}) => {
  const { siteModel } = useSiteModelContext()
  const { convertCartographicToPosition } = useTilesetMapFeatureHelpers()
  const { camera } = useThree()

  const flightRoute = useRef<
    | {
        start: {
          pos: Vector3
          rot: Quaternion
        }
        end: {
          pos: Vector3
          rot: Quaternion
        }
      }
    | undefined
  >(undefined)
  const flightProgress = useRef(0)

  // Get the feature primitive the camera is flying to (unitIDFlyingTo), and define the route
  const featurePrimitiveFlyingTo = useMemo(() => {
    if (!unitIDFlyingTo) return undefined
    const siteFeature = Object.values(siteModel.data.features).find(
      ({ unit_id }) => unit_id === unitIDFlyingTo
    )
    if (!siteFeature) return undefined

    const sitePrimitive = isSiteFeaturePrimitive(siteFeature)
      ? (siteFeature as SiteFeaturePrimitive)
      : undefined

    if (!sitePrimitive) return undefined

    // Get Flight Route
    const featurePrimitivePos = convertCartographicToPosition(
      sitePrimitive.coordinate
    )

    const cameraAsPerspective = camera as PerspectiveCamera

    const start = {
      pos: cameraAsPerspective.position.clone(),
      rot: cameraAsPerspective.quaternion.clone(),
    }
    const cameraFovInRads = !cameraAsPerspective.fov
      ? undefined
      : MathUtils.degToRad(cameraAsPerspective.fov)

    if (isNil(cameraFovInRads)) {
      throw new Error(
        'Camera FOV is undefined in flight handler -- this is likely because a new camera type was introduced'
      )
    }

    const cameraAspect = cameraAsPerspective.aspect

    if (isNil(cameraAspect)) {
      throw new Error(
        'Camera aspect is undefined in flight handler -- this is likely because a new camera type was introduced'
      )
    }

    const featureMaxSideLength = getFeatureMaxSideLength(sitePrimitive)

    const cameraOffset =
      Math.abs(featureMaxSideLength) /
      Math.tan(cameraFovInRads / 2) /
      cameraAsPerspective.aspect

    // Create a fake object in order to do lookAt() math correctly
    const fauxObject = new Object3D()
    fauxObject.updateMatrixWorld(true)
    fauxObject.lookAt(new Vector3(1, 1, 1))
    fauxObject.position.set(
      featurePrimitivePos.x + cameraOffset,
      featurePrimitivePos.y + cameraOffset,
      featurePrimitivePos.z + cameraOffset
    )

    const end = {
      pos: fauxObject.position,
      rot: fauxObject.quaternion,
      extra: featurePrimitivePos,
    }

    flightRoute.current = { start, end }
    flightProgress.current = 0

    return sitePrimitive
  }, [
    camera,
    convertCartographicToPosition,
    siteModel.data.features,
    unitIDFlyingTo,
  ])

  useFrame(({ camera }, delta) => {
    // Not currently flying
    if (!featurePrimitiveFlyingTo || !flightRoute.current) return

    // Flight finished
    if (flightProgress.current >= 1) {
      flightRoute.current = undefined
      flightProgress.current = 0
      setUnitIDFlyingTo(undefined)
      return
    }

    // In flight
    flightProgress.current = Math.min(flightProgress.current + 0.8 * delta, 1)
    const smoothT = MathUtils.smoothstep(flightProgress.current, 0, 1)
    camera.position.lerpVectors(
      flightRoute.current.start.pos,
      flightRoute.current.end.pos,
      smoothT
    )
    camera.quaternion.slerpQuaternions(
      flightRoute.current.start.rot,
      flightRoute.current.end.rot,
      smoothT
    )
  })

  return <></>
}

function getFeatureMaxSideLength(feature: SiteFeaturePrimitive): number {
  switch (feature.kind) {
    case 'sphere': {
      const sphere = feature as SiteFeaturePrimitiveSphere
      return sphere.geometry.radius * 2
    }
    case 'cylinder': {
      const cylinder = feature as SiteFeaturePrimitiveCylinder
      return Math.max(
        cylinder.geometry.bottom_radius * 2,
        cylinder.geometry.top_radius * 2,
        cylinder.geometry.height
      )
    }
    case 'box': {
      const box = feature as SiteFeaturePrimitiveBox
      return Math.max(
        box.geometry.width,
        box.geometry.depth,
        box.geometry.height
      )
    }
    default: {
      throw new Error(
        `Unsupported feature kind: ${feature}. Expected 'sphere', 'cylinder', or 'box'.`
      )
    }
  }
}
